import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { FieldRenderProps } from 'react-final-form';
import cn from 'classnames';

import { FieldProps } from '../Field';
import Autocomplete from '../../../../Autocomplete';
import { TravellerFieldEnum, TravellerProfileLightFragment } from '@websky/graphql';
import { EngineTheme, ThemeProvider, useTheme } from '../../../../theme';
import { getTravellerFullName, getUserValue } from '../../../../utils';
import { CdnIcon } from '@shared/ui';
import { useTranslation } from 'react-i18next';
import { isWithinInterval } from 'date-fns';
import { FillValue } from '../../../../Checkout/components/Checkout/Form/types';
import { Group } from '../../../../Autocomplete/types';
import CompanionItem from './CompanionItem/CompanionItem';
import CompanionGroup from './CompanionGroup/CompanionGroup';
import { NameField } from '../../../types';

interface Props {
	field: NameField;
	label: string;
	error: string;
	fieldState: FieldRenderProps<string, HTMLInputElement>;
	onFillFromDocument?: React.Dispatch<FillValue>;
	onBlur?: () => void;
	variant?: 'standard' | 'filled' | 'outlined';
	onSwap?: () => void;
	onClear?: React.DispatchWithoutAction;
}

const LastName: React.FC<FieldProps & Props> = memo(props => {
	const {
		label,
		field,
		fieldState,
		onChange,
		className,
		error,
		onBlur,
		disabled,
		variant,
		autofocus,
		onSwap,
		onFillFromDocument
	} = props;

	const { LastName: theme } = useTheme('Passenger');
	const { Input: inputCss } = useTheme('Input');
	const { t } = useTranslation('Passenger');

	const isLastName = field.type === TravellerFieldEnum.LastName;

	const options = useMemo(() => {
		const profiles = field.travellerProfiles ?? [];

		if (!profiles.length) {
			return [];
		}

		const search = fieldState.input.value?.toLowerCase() ?? '';

		return profiles.filter(traveller => {
			const filterByBirthDate = isWithinInterval(
				new Date(getUserValue(traveller, TravellerFieldEnum.BirthDate)),
				{
					start: field.maxBirthDates.min,
					end: field.maxBirthDates.max
				}
			);

			const isFilledLastNameAndFirstName = traveller.values.some(
				value =>
					(value.type === TravellerFieldEnum.LastName || value.type === TravellerFieldEnum.FirstName) &&
					!value.value
			);

			const matchesSearch =
				!search ||
				traveller.documents.some(
					doc =>
						getUserValue(doc, TravellerFieldEnum.LastName)?.toLowerCase().includes(search) ||
						getUserValue(doc, TravellerFieldEnum.FirstName)?.toLowerCase().includes(search)
				);

			return filterByBirthDate && !isFilledLastNameAndFirstName && matchesSearch;
		});
	}, [field.travellerProfiles, field.maxBirthDates, fieldState.input.value]);

	const travellersById = useMemo(() => {
		return options.reduce((acc, traveller) => {
			acc[traveller.id] = traveller;

			return acc;
		}, {} as Record<string, TravellerProfileLightFragment>);
	}, [options]);

	const onChangeHandler = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;

			onChange?.(field.name, value);

			(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(value);
			onBlur?.();
		},
		[onChange, fieldState]
	);

	const autocompleteTheme = useMemo<EngineTheme>(
		() => ({
			Autocomplete: {
				Autocomplete: {
					disabled: inputCss.disabled,
					inner_input: inputCss.input
				}
			}
		}),
		[]
	);

	const optionGroups = useMemo<Group<FillValue>[]>(() => {
		const groups: Group<FillValue>[] = [];

		for (const option of options) {
			const traveller = travellersById[option.id];
			const documents = traveller.documents.map(document => ({
				label: option.id,
				value: { traveller, document }
			}));

			if (documents.length === 0) {
				continue;
			}

			groups.push({
				label: (
					<>
						<span>{getTravellerFullName(traveller)}</span>&nbsp;
						<span
							className={cn(theme.documentCount, {
								[theme.documentCount_single]: documents.length === 1
							})}
						>
							{t('documentsCount', { count: documents.length })}
						</span>
					</>
				),
				options: documents
			});
		}

		return groups;
	}, [options, travellersById, t, fieldState.input.value]);

	return (
		<ThemeProvider value={autocompleteTheme}>
			<div
				className={cn(theme.field, {
					[theme.field_swapRight]: onSwap && isLastName,
					[theme.field_swapLeft]: onSwap && !isLastName
				})}
			>
				<Autocomplete<FillValue>
					dropDownClassName={theme.dropdown}
					name={field.name}
					label={label}
					variant={variant}
					autofocus={autofocus}
					inputValue={fieldState.input.value}
					onSelect={onFillFromDocument}
					onInputChange={onChangeHandler}
					className={className}
					error={error}
					optionRenderer={(option, key, onClick) => {
						return (
							<CompanionItem
								key={key}
								nationalities={field.nationalities}
								document={option.value.document}
								companion={option.value.traveller}
								onClick={onClick}
							/>
						);
					}}
					groupRenderer={(group, _, optionRenderer) => {
						const groupKey = `${group.options[0]?.value.traveller.id}-${group.options
							.map(option => option.value.document.number + option.value.document.type)
							.join(',')}`;

						return <CompanionGroup key={groupKey} companion={group} optionRender={optionRenderer} />;
					}}
					options={optionGroups}
					disabled={disabled}
				/>
				{onSwap && isLastName && (
					<div className={theme.swap} onClick={onSwap}>
						<CdnIcon icon="Action/Sort" />
					</div>
				)}
			</div>
		</ThemeProvider>
	);
});

export default LastName;
